<script lang="ts">
  import CardList from '@athena-lib/Card/CardList/CardList.svelte';
  import { Checkbox } from '@athena-lib/Checkbox';
  import ListGroupSelectable from '@athena-lib/ListGroupSelectable/ListGroupSelectable.svelte';
  import { ProductWizardTextField } from '@athena-lib/Fields';
  import { Button } from '@athena-lib/Button';
  import { SizeBox, type SizeUpdatedEvent } from '@athena-lib/SizeBox';
  import clsx from 'clsx';
  import ProgressBar from '@athena-lib/ProgressBar/ProgressBar.svelte';
  import debouncedFunc from '@athena-lib/timing-utils';
  import { HeadingMedium, HeadingSmall, Text } from '@athena-lib/Text';
  import {
    Grid,
    GridItem,
    type GridColumns,
    type GridColumnSpan,
    type ResponsiveGridColumns,
  } from '@athena-lib/Grid';
  import { Popover } from '@athena-lib/Popover';
  import { getBgColorFromFile } from '@athena-lib/image-utils';
  import {
    getColorContext,
    initializeColorContext,
    type PageSectionBackground,
  } from '@athena-lib/container-context.svelte';
  import { objectEntries } from '@athena-lib/object-utils';
  import { type Responsive } from '@athena-lib/responsive-utils';
  import { Dropdown, DropdownItem } from '@athena-lib/Fields/ProductWizardDropdown';
  import type { DeviceName } from '@athena-lib/device';
  import type { AttributesMap, AttributeValueSingle, ProductItem } from '@stickerapp-org/nomisma';
  import { ProductWizardContext, type AttributeValue } from '@stickerapp-org/nomisma';
  import type { ListGroupOption } from '../ListGroupSelectable/types';
  import {
    type FileAppData,
    StickerWizardProductsEnum,
    type StickerWizardData,
    type WizardStates,
    type CustomValues,
    type Orders,
    type Materials,
    type SizeTypes,
  } from './types';
  import { StickerWizardService } from './service/StickerWizardService';
  import TranslationService from './product/service/TranslationService';
  import { UploadImageService } from './service/UploadImageService';
  import type { PriceAndQuantityOptionInterface } from './model/PriceAndQuantityOptionInterface';

  const {
    data,
    background,
    materialPresetOnly,
    canSendFileLater = false,
    forceUploadFile = false,
    buttonSize = 'medium',
    canUpload = true,
    class: classList,
    columns = {
      sm: 1,
      md: 1,
      lg: 6,
    },
    alignment = 'none',
    title,
    description,
    stickyProductList,
    staticQuantity,
    colOrder = ['product', 'shape', 'material', 'laminate', 'size', 'quantity'],
    staticSize,
    verticalAlignment,
    availableMaterials,
    sizeType,
    onContinue,
  }: {
    data: StickerWizardData;
    background: PageSectionBackground;
    class?: string;
    materialPresetOnly?: boolean;
    canSendFileLater?: boolean;
    forceUploadFile?: boolean;
    buttonSize?: 'small' | 'medium';
    canUpload?: boolean;
    columns?: GridColumns | ResponsiveGridColumns;
    title?: string;
    description?: string;
    alignment?: 'none' | 'left' | 'right';
    stickyProductList?: boolean;
    staticQuantity?: number;
    colOrder?: Orders;
    staticSize?: boolean;
    verticalAlignment?: boolean;
    availableMaterials?: Materials;
    sizeType?: SizeTypes;
    onContinue: (
      item: ProductItem,
      price?: PriceAndQuantityOptionInterface,
      file?: FileAppData,
      customValues?: CustomValues,
    ) => void;
  } = $props();

  type Columns = {
    productCol: Partial<Record<DeviceName, GridColumnSpan>>;
    shapeCol: Partial<Record<DeviceName, GridColumnSpan>>;
    materialCol: Partial<Record<DeviceName, GridColumnSpan>>;
    laminateCol: Partial<Record<DeviceName, GridColumnSpan>>;
    sizeCol: Partial<Record<DeviceName, GridColumnSpan>>;
    quantityCol: Partial<Record<DeviceName, GridColumnSpan>>;
  };
  const service: StickerWizardService = new StickerWizardService(data.baseApiUrl);
  const fileService: UploadImageService = new UploadImageService(data.fileappUrl);
  const sizeBoxStep = 0.1;
  const customQuantityStep = 1;
  const idQuantityInput = Math.floor(Math.random() * 10000).toString();

  const derivedData = $derived(data);
  let context: ProductWizardContext = new ProductWizardContext();
  let fileInput: HTMLInputElement | undefined = $state();

  // IMPORTANT: Reset quantity before reset the product to not change the products init value of quantity
  context.resetQuantity(data.productWizardData.pricesAndQuantity);
  context.reset(
    data.productWizardData.productItem,
    data.productWizardData.attributes as AttributesMap,
    true,
  );
  context.invokeProcessors();
  const productItem = $state(context.productItem);
  // All state variables
  let states: WizardStates = $state({
    canUpload: canSendFileLater || (canUpload && productItem.getAttribute('sheet_name') === 'path'),
    customSizeOpen: false,
    customQuantityOpen: false,
    price: data.productWizardData.price,
    customQuantityInvalid: false,
    uploadProgressPercentage: 0,
    selectedLaminate: '',
    shapeList: [],
    materialList: [],
    laminateList: [],
    sizeList: [],
    pricesAndQuantity: [],
    showVat: data.includeVat ?? true,
    selectedQuantity: '',
    productName: '',
    productItem: productItem.getProductName(),
  });

  const productList = $derived.by(() => {
    const products =
      data.productList.length === 0 ? Object.values(StickerWizardProductsEnum) : data.productList;

    return products.map((value) => ({
      key: value,
      value: TranslationService.getTranslationFromMap(
        data.translations.strings,
        'products',
        'product_name',
        value,
      ),
    }));
  });

  const laminateList = $derived.by(() => {
    return states.laminateList.map((laminate) => ({
      key: laminate.value,
      value: laminate.title,
      disabled: laminate.disabled,
    }));
  });

  // Get background color
  const backGroundClass = $derived.by(() => {
    const backgrounds: Record<string, string> = {
      light: clsx('bg-st-gray-100'),
      medium: clsx('bg-st-gray-100'),
      white: clsx('bg-white'),
      none: clsx('bg-transparent'),
      dark: clsx('bg-st-black'),
    };
    return backgrounds[background];
  });
  if (canSendFileLater) {
    initializeColorContext({
      lg: background,
      md: background,
      sm: background,
    });
  }
  const contextBackground = getColorContext();

  const responsiveColumns: Columns = $derived.by(() => {
    return {
      productCol: {
        sm: 1,
        md: 1,
        lg: 3,
      },
      shapeCol: {
        sm: 1,
        md: 1,
        lg: verticalAlignment ? 1 : 3,
      },
      materialCol: {
        sm: 1,
        md: 1,
        lg: verticalAlignment ? 1 : 4,
      },
      laminateCol: {
        sm: 1,
        md: 1,
        lg: 1,
      },
      sizeCol: {
        sm: 1,
        md: 1,
        lg: verticalAlignment ? 1 : 2,
      },
      quantityCol: {
        sm: 1,
        md: 1,
        lg: verticalAlignment ? 1 : 3,
      },
    };
  });

  const productShapeColHidden = () => {
    return (!stickyProductList && productList.length <= 1) || !colOrder.includes('product');
  };

  const colors = $derived.by(() => {
    const responsiveBgDropdown: Responsive<string> = {
      lg: contextBackground.value.lg === 'dark' ? 'lg:bg-st-gray-800' : 'lg:bg-st-white lg:border',
      md: contextBackground.value.md === 'dark' ? 'md:bg-st-gray-800' : 'md:bg-st-white md:border',
      sm: contextBackground.value.sm === 'dark' ? 'bg-st-gray-800' : 'bg-st-white border',
    };

    const responsiveBgAndBorder: Responsive<string> = {
      lg:
        contextBackground.value.lg === 'dark'
          ? 'lg:border-st-gray-800 lg:bg-st-gray-800'
          : 'lg:border-st-gray-200 lg:bg-st-gray-200',
      md:
        contextBackground.value.md === 'dark'
          ? 'md:border-st-gray-800 md:bg-st-gray-800'
          : 'md:border-st-gray-200 md:bg-st-gray-200',
      sm:
        contextBackground.value.sm === 'dark'
          ? 'border-st-gray-800 bg-st-gray-800'
          : 'border-st-gray-200 bg-st-gray-200',
    };
    return {
      bgDropdown: objectEntries(responsiveBgDropdown)
        .map(([_, value]) => value)
        .join(' '),
      bgAndBorder: objectEntries(responsiveBgAndBorder)
        .map(([_, value]) => value)
        .join(' '),
    };
  });

  // Since we can change the material preset in CMS we need to effect on the derived state
  $effect(() => {
    states.materialList = getOptionsForAttribute('material', data.materialPreset);
    states.productName = getProductNameTranslation();
    // Re-init data if default product change
    if (
      context.productItem.getProductName() !== derivedData.productWizardData.productItem.productName
    ) {
      data.productWizardData = derivedData.productWizardData;
      initializeData();
    }
  });

  states.shapeList = getOptionsForAttribute('sheet_name');
  states.laminateList = getOptionsForAttribute('laminate', data.laminatePreset);
  states.sizeList = getSizeOptions();
  states.pricesAndQuantity = updatePriceList();
  states.selectedLaminate = getLaminate();

  const debounceValidateAndRefreshPrices = debouncedFunc(validateAndRefreshPrices, 500);

  // Get alignment
  const alignmentClass = $derived.by(() => {
    const alignments: Record<string, string> = {
      none: clsx('float-none'),
      left: clsx('float-left'),
      right: clsx('float-right'),
    };
    return alignments[alignment];
  });

  function initializeData() {
    context.isBusy = true;
    states.customSizeOpen = false;
    states.customQuantityOpen = false;
    context.reset(
      data.productWizardData.productItem,
      data.productWizardData.attributes as AttributesMap,
      true,
    );
    context.invokeProcessors();
    context.resetQuantity(data.productWizardData.pricesAndQuantity);

    states.price.totalFormatted = data.productWizardData.price.totalFormatted;
    states.canUpload =
      canSendFileLater || (canUpload && productItem.getAttribute('sheet_name') === 'path');

    states.materialList = getOptionsForAttribute('material', data.materialPreset);
    states.shapeList = getOptionsForAttribute('sheet_name');
    states.laminateList = getOptionsForAttribute('laminate', data.laminatePreset);
    states.sizeList = getSizeOptions();
    states.pricesAndQuantity = updatePriceList();
    states.selectedLaminate = getLaminate();
    states.productName = getProductNameTranslation();
    context.isBusy = false;
  }

  function updatePriceList() {
    const allowCustom = !context.attrComputer.isInFilteredValues('fixed_quantity', true);
    const quantity =
      (context.cherryPicks.getAttribute('quantity') as number) ||
      productItem.getAttribute('quantity');

    states.selectedQuantity =
      quantity && quantity < getMinQuantity()
        ? context.quantityAndPriceOptions[0].quantity
        : quantity;
    let existsInPriceListIndex = context.quantityAndPriceOptions.findIndex(
      (x) => x.quantity == states.selectedQuantity,
    );
    if (existsInPriceListIndex < 0) {
      states.customQuantityOpen = allowCustom;
      if (!allowCustom) {
        states.selectedQuantity = context.quantityAndPriceOptions[0].quantity;
      }
    }

    productItem.setAttribute('quantity', states.selectedQuantity as AttributeValue);
    const priceList: ListGroupOption[] = [];
    context.quantityAndPriceOptions.map((opt: PriceAndQuantityOptionInterface) => {
      priceList.push({
        value: opt.quantity.toString(),
        formattedValue: opt.quantityFormatted,
        title: opt.price,
        save: opt.save > 0 ? opt.save.toString() : undefined,
        selected: opt.quantity == states.selectedQuantity && !states.customQuantityOpen,
      });
    });
    if (allowCustom) {
      priceList.push({
        value: 'custom_quantity',
        title: '',
        formattedValue: data.translations.strings['custom_quantity'],
        selected: states.customQuantityOpen,
      });
    }
    return priceList;
  }

  function getLaminate() {
    let laminate =
      (context.cherryPicks.getAttribute('laminate') as string) ||
      productItem.getAttribute('laminate');

    let index = states.laminateList.findIndex((x) => x.value === laminate);
    if (laminate && index >= 0) {
      return laminate;
    }
    return states.laminateList[0]?.value;
  }

  function getOptionsForAttribute(attributeName: string, preset?: AttributeValueSingle) {
    let options = context.getAttributeOptions(attributeName, true);
    if (preset) {
      const presetOption = options.find((option) => option.value === preset);
      if (presetOption) {
        options =
          materialPresetOnly && attributeName === 'material'
            ? [presetOption]
            : [presetOption, ...options.filter((option) => option.value !== preset)];
        preset = presetOption?.value;
      }
    }

    // Filter and sort based on order
    if (availableMaterials && availableMaterials?.length > 0 && attributeName === 'material') {
      options = options
        .filter((option) => availableMaterials.includes(option.value.toString()))
        .sort((a, b) => {
          return (
            availableMaterials.indexOf(a.value.toString()) -
            availableMaterials.indexOf(b.value.toString())
          );
        });
    }

    // Get init value and check if it exists in options
    const initValue = (context.cherryPicks.getAttribute(attributeName) as string) || preset;
    const index = options.findIndex((opt) => opt.value === initValue);

    if (initValue && index >= 0) {
      productItem.setAttribute(attributeName, initValue);
    }
    const listGroupOptions: ListGroupOption[] = [];
    options.map((opt) => {
      const optionIsOutOfStock = context.attrComputer.isOutOfStock(attributeName, opt.value);
      if (optionIsOutOfStock) {
        opt.disabled = true;
      }

      opt.selected = opt.value === productItem.getAttribute(attributeName) || options.length == 1;
      listGroupOptions.push({
        title: data.translations.strings[`products_${attributeName}_${opt.value.toString()}`],
        value: opt.value,
        icon: opt.icon,
        image: {
          url: opt.icon,
          // Set empty alt text for material icons
          alt: attributeName === 'material' && opt.icon ? '' : opt.value.toString(),
        },
        selected: opt.selected,
      });
    });
    return listGroupOptions;
  }

  function updateAndEveluate(
    list: ListGroupOption[],
    option: ListGroupOption,
    attributeName: string,
  ) {
    list.map((x: ListGroupOption) => {
      if (x.value === option.value) {
        x.selected = true;
      }
      if (x.selected && x.value !== option.value) {
        x.selected = false;
      }
    });
    if (option.selected) {
      let value =
        option.value === 'custom_quantity'
          ? parseInt(productItem.getAttribute('quantity') ?? '0')
          : option.value;
      context.cherryPicks.setAttribute(attributeName, value);
      productItem.setAttribute(attributeName, value);
    } else {
      context.cherryPicks.removeAttribute(attributeName, option.value);
      productItem.removeAttribute(attributeName);
    }
    evaluate();
  }

  function onChange(type: string, option: ListGroupOption) {
    switch (type) {
      case 'laminate':
        updateAndEveluate(states.laminateList, option, type);
        break;
      case 'material':
        updateAndEveluate(states.materialList, option, type);
        break;
      case 'sheet_name':
        updateAndEveluate(states.shapeList, option, type);
        states.canUpload = canSendFileLater || (canUpload && option.value === 'path');
        break;
      case 'size':
        states.customSizeOpen = option.value === 'custom_size';
        if (states.customSizeOpen) {
          states.sizeList = getSizeOptions();
          return;
        }
        context.productItem.setAttribute(
          'width_mm',
          context.sizeHelper.localizedToMM(
            context.sizeHelper.extractMeassuresFromString(option.value.toString()).width,
          ),
        );
        context.productItem.setAttribute(
          'height_mm',
          context.sizeHelper.localizedToMM(
            context.sizeHelper.extractMeassuresFromString(option.value.toString()).height,
          ),
        );
        context.productItem.setAttribute('size', parseInt(option.value.toString()));
        updateAndEveluate(states.sizeList, option, type);
        break;
      case 'quantity':
        states.customQuantityOpen = option.value === 'custom_quantity';
        updateAndEveluate(states.pricesAndQuantity, option, type);
        break;
    }
  }

  // When the whole product changes
  async function onProductChange(productName: string) {
    if (!context.isBusy) {
      context.isBusy = true;

      states.productItem = productName;
      const material =
        (context.cherryPicks.getAttribute('material') as string) ||
        data.productWizardData.materialPreset ||
        data.materialPreset;

      try {
        const response = await service.fetchWizardData(productName, data.translations.lang, {
          material: material,
          showVat: data.includeVat,
          quantity: productItem.getAttribute('quantity'),
        });
        data.productWizardData = response;
        // Re-init wizard
        initializeData();
      } catch (e) {
        console.error(e);
      }
    }
  }

  function onLaminateChange(laminateValue: string) {
    const laminate = states.laminateList.find((l) => l.value === laminateValue);
    if (laminate) {
      return onChange('laminate', laminate);
    }
  }

  async function evaluate() {
    context.invokeProcessors();
    await validateAndRefreshPrices(true);
  }

  async function validateAndRefreshPrices(update?: boolean) {
    try {
      const item = await service.fetchProductItem(
        productItem,
        data.translations.lang ?? 'us',
        states.showVat,
      );
      states.price = item.price;
      context.resetQuantity(item.price_list);
      if (update) {
        states.materialList = getOptionsForAttribute('material', data.materialPreset);
        states.shapeList = getOptionsForAttribute('sheet_name');
        states.laminateList = getOptionsForAttribute('laminate', data.laminatePreset);
        states.sizeList = getSizeOptions();
        if (!staticQuantity) {
          states.pricesAndQuantity = updatePriceList();
        }
        states.selectedLaminate = getLaminate();
      }
    } catch (e) {
      console.error(e);
    }
  }

  function getSizeOptions(): ListGroupOption[] {
    let options = context.getAttributeOptions('size', true);
    options = options.filter((opt) => {
      return opt.value !== 'custom_size';
    });

    let selected = context.cherryPicks.getAttribute('size') || productItem.getAttribute('size');
    if (selected === 'custom_size') {
      states.customSizeOpen = true;
    }
    const existsInSizeListIndex = options.findIndex((s) => s.value == selected);
    if (existsInSizeListIndex < 0) {
      selected = options[0]?.value;
    }
    const listGroupOptions: ListGroupOption[] = [];
    options.map((opt) => {
      opt.selected = opt.value == selected && !states.customSizeOpen;
      listGroupOptions.push({
        title: opt.value.toString(),
        value: opt.value,
        selected: opt.selected,
      });
      return opt;
    });
    if (
      !context.sizeHelper.isSizeFixed() &&
      sizeType !== 'list' &&
      !options.some((opt) => opt.value === 'custom_size')
    ) {
      listGroupOptions.push({
        title: data.translations.strings['custom_size'],
        value: 'custom_size',
        selected: states.customSizeOpen,
      });
    }
    return listGroupOptions;
  }

  function getMinQuantity() {
    let minQuantity = 1;
    if (context.quantityAndPriceOptions && context.quantityAndPriceOptions.length) {
      minQuantity = +context.quantityAndPriceOptions.reduce((prev, current) => {
        return prev.quantity < current.quantity ? prev : current;
      }).quantity;
    }
    return minQuantity;
  }

  // Custom size changes
  async function onCustomSizeChange(data: SizeUpdatedEvent) {
    if (!data.customValue) {
      return;
    }

    let newValueMM = context?.sizeHelper.setMeasureAttrLocalized(
      data.customValue?.name,
      data.customValue?.measure,
    );

    let oppositeMeassureAttr = data.customValue?.name == 'width_mm' ? 'height_mm' : 'width_mm';

    if (
      context?.sizeHelper.hasMaxSizeOtherSide() &&
      [newValueMM, context?.sizeHelper.getSizeByAttribute(oppositeMeassureAttr)].every(
        (size) => size > context?.sizeHelper.maxSizeOtherSide,
      )
    ) {
      context?.sizeHelper.setMeasureAttrInMM(
        oppositeMeassureAttr,
        context?.sizeHelper.maxSizeOtherSide.localized,
      );
    }

    await debounceValidateAndRefreshPrices(true);
  }

  async function onCustomQuantityBlur(event: Event) {
    let measure: number = parseFloat((event.target as HTMLInputElement).value);
    const min = getMinQuantity();
    if (isNaN(measure) || measure < min) {
      (event.target as HTMLInputElement).value = min.toString();
      onCustomQuantityChange(event);
    }
  }

  // Custom quantity changes
  async function onCustomQuantityChange(event: Event) {
    const newValue = parseInt((event.target as HTMLInputElement).value);
    if (isNaN(newValue)) {
      return;
    }

    states.customQuantityInvalid = newValue < getMinQuantity() ? true : false;

    if (states.customQuantityInvalid) {
      return;
    }

    context.cherryPicks.setAttribute('quantity', newValue);
    productItem.setAttribute('quantity', newValue);
    await debounceValidateAndRefreshPrices();
  }

  // Handle file upload
  async function onDropFile(e: DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer?.files[0];
    customCSS(e, false);
    if (file) {
      await uploadFile(file, e);
    }
  }

  async function onUploadFile(e: Event) {
    e.preventDefault();
    const file = fileInput?.files?.[0];
    if (file && fileInput) {
      await uploadFile(file, e);
      fileInput.value = '';
    }
  }

  async function uploadFile(file: File, event: Event) {
    if (!file) {
      return;
    }

    try {
      context.isBusy = true;
      const uploadedFile = await fileService.uploadFileToUrl(file, (progress) => {
        states.uploadProgressPercentage = progress;
      });
      uploadedFile.background = await getBgColorFromFile(file);
      states.uploadProgressPercentage = 0;
      context.isBusy = false;
      if (staticQuantity) {
        productItem.getAttributes()['quantity'] = staticQuantity || states.price.quantity;
        await validateAndRefreshPrices();
        productItem.getAttributes()['quantity'] = staticQuantity || states.price.quantity;
      }

      productItem.getAttributes()['size'] = states.customSizeOpen
        ? 'custom_size'
        : productItem.getAttributes()['size'];

      onContinue(productItem, states.price, uploadedFile, {
        customSize: states.customSizeOpen, // For GTM
        customQuantity: states.customQuantityOpen, // For GTM
        uploadEvent: event.type,
      });
    } catch (e) {
      console.error(e);
      context.isBusy = false;
    }
  }

  function customCSS(e: Event, add: boolean) {
    e.preventDefault();
    let rootElem = document.getElementById('wizform');
    if (add) {
      rootElem?.classList.replace('border-transparent', 'border-st-gray-400');
    } else {
      rootElem?.classList.replace('border-st-gray-400', 'border-transparent');
    }
  }

  async function onWizardClose() {
    if (staticQuantity) {
      productItem.getAttributes()['quantity'] = staticQuantity || states.price.quantity;
      await validateAndRefreshPrices();
      productItem.getAttributes()['quantity'] = staticQuantity || states.price.quantity;
    }

    productItem.getAttributes()['size'] = states.customSizeOpen
      ? 'custom_size'
      : productItem.getAttributes()['size'];

    if (data.productWizardData.file) {
      return onContinue(productItem, states.price, data.productWizardData.file, {
        customSize: states.customSizeOpen,
        customQuantity: states.customQuantityOpen,
      });
    }
    const icons = context.attrComputer.getIcons('sheet_name');
    const iconUrl = icons[productItem.getAttributes().sheet_name as string];
    let urls = iconUrl.split(/\.(?=[^.]+$)/); // Split on last dot
    const previewUrl = `${urls[0]}_preview.${urls[1]}`; // Concat string preview URL
    productItem.setAttribute('icon', iconUrl);
    productItem.setAttribute('previewIcon', previewUrl);
    return onContinue(productItem, states.price, undefined, {
      customSize: states.customSizeOpen,
      customQuantity: states.customQuantityOpen,
    });
  }

  function getProductNameTranslation() {
    return TranslationService.getTranslationFromMap(
      data.translations.strings,
      'products',
      'product_name',
      productItem.getProductName(),
    );
  }
</script>

{#snippet product()}
  <GridItem colSpan={responsiveColumns.productCol}>
    {#if productList.length > 1 || stickyProductList}
      <HeadingSmall id="product-list-heading" class="pb-3" size="xsmall">
        {data.translations.strings['products_product_wizard_product'] ?? states.productName}
      </HeadingSmall>
      <Dropdown
        labelledBy="product-list-heading"
        value={states.productItem}
        onChange={onProductChange}
        class="bg-white"
      >
        {#each productList as item}
          <DropdownItem label={item.value} value={item.key}>
            {item.value}
          </DropdownItem>
        {/each}
      </Dropdown>
    {/if}
    {@render shape()}
  </GridItem>
{/snippet}

{#snippet shape()}
  <GridItem colSpan={responsiveColumns.shapeCol}>
    <div class:pt-5={productList.length > 1 || stickyProductList}>
      <HeadingSmall id="shape-list-heading" class="pb-3" size="xsmall">
        {TranslationService.getTranslationFromMap(
          data.translations.strings,
          'products',
          'product_wizard',
          'shape',
        )}
      </HeadingSmall>
      <ListGroupSelectable
        name="shape"
        options={states.shapeList}
        disabled={false}
        onChange={(e: ListGroupOption) => onChange('sheet_name', e)}
        aria-labelledby="shape-list-heading"
      ></ListGroupSelectable>
    </div>
  </GridItem>
{/snippet}

{#snippet material()}
  <GridItem colSpan={responsiveColumns.materialCol}>
    <HeadingSmall id="material-list-heading" class="pb-3" size="xsmall">
      {TranslationService.getTranslationFromMap(
        data.translations.strings,
        'products',
        'product_wizard',
        'material',
      )}
    </HeadingSmall>
    <div class="cards">
      <CardList
        class={clsx(
          'max-h-96 grid-flow-col grid-cols-none overflow-x-auto',
          verticalAlignment
            ? '!grid-cols-none overflow-y-hidden'
            : 'md:gap-3 lg:grid-flow-row lg:grid-cols-12 lg:gap-1 lg:overflow-x-hidden',
        )}
        options={states.materialList}
        disabled={false}
        columns={{
          sm: 1,
          md: 1,
          lg: states.materialList.length === 1 ? 1 : 3,
        }}
        space={verticalAlignment ? 'gutter' : 'small'}
        disabledTranslation={data.translations.strings['out_of_stock']}
        onChange={(e: ListGroupOption) => onChange('material', e)}
        aria-labelledby="material-list-heading"
        tabindex={-1}
      ></CardList>
    </div>
    {#if !verticalAlignment}
      <div>
        <HeadingSmall id="finish-heading" class="pb-3" size="xsmall">
          {TranslationService.getTranslationFromMap(
            data.translations.strings,
            'products',
            'product_wizard',
            'finish',
          )}
        </HeadingSmall>
        <Dropdown
          labelledBy="finish-heading"
          value={states.selectedLaminate.toString()}
          onChange={onLaminateChange}
          class="bg-white"
        >
          {#each laminateList as item}
            <DropdownItem
              label={item.value?.toString() ?? ''}
              value={item.key.toString()}
              class="flex items-center justify-between"
              disabled={item.disabled}
            >
              <span class:text-gray-400={item.disabled}>{item.value}</span>
              {#if item.disabled}
                <span class="bg-red-400 p-1 text-center">
                  <Text color="light" size="small" element="span" bold={true}>
                    {data.translations.strings['out_of_stock']}
                  </Text>
                </span>
              {/if}
            </DropdownItem>
          {/each}
        </Dropdown>
      </div>
    {/if}
  </GridItem>
{/snippet}

{#snippet laminate()}
  <GridItem colSpan={responsiveColumns.laminateCol}>
    <div>
      <HeadingSmall id="finish-heading" class="pb-3" size="xsmall">
        {TranslationService.getTranslationFromMap(
          data.translations.strings,
          'products',
          'product_wizard',
          'finish',
        )}
      </HeadingSmall>
      <Dropdown
        labelledBy="finish-heading"
        value={states.selectedLaminate.toString()}
        onChange={onLaminateChange}
        class="bg-white"
      >
        {#each laminateList as item}
          <DropdownItem
            label={item.value?.toString() ?? ''}
            value={item.key.toString()}
            class="flex items-center justify-between"
            disabled={item.disabled}
          >
            <span class:text-gray-400={item.disabled}>{item.value}</span>
            {#if item.disabled}
              <span class="bg-red-400 p-1 text-center">
                <Text color="light" size="small" element="span" bold={true}>
                  {data.translations.strings['out_of_stock']}
                </Text>
              </span>
            {/if}
          </DropdownItem>
        {/each}
      </Dropdown>
    </div>
  </GridItem>
{/snippet}

{#snippet size()}
  <GridItem colSpan={responsiveColumns.sizeCol}>
    <HeadingSmall id="size-heading" class="pb-3" size="xsmall">
      {TranslationService.getTranslationFromMap(
        data.translations.strings,
        'products',
        'product_wizard',
        'size',
      )}
    </HeadingSmall>
    {#if sizeType !== 'custom'}
      <ListGroupSelectable
        name="size"
        options={states.sizeList}
        disabled={staticSize ?? false}
        onChange={(e: ListGroupOption) => onChange('size', e)}
        aria-labelledby="size-heading"
      ></ListGroupSelectable>
    {/if}
    {#if states.customSizeOpen || sizeType === 'custom'}
      <div class={clsx(verticalAlignment ? '' : 'pt-2')}>
        <SizeBox
          class={colors.bgAndBorder}
          step={sizeBoxStep}
          max={context.sizeHelper.getSizeByMM(context.sizeHelper.maxSize.localized).mm}
          min={context.sizeHelper.getSizeByMM(context.sizeHelper.minSize.localized).mm}
          width={context.sizeHelper.getSizeByAttribute('width_mm').localized}
          height={context.sizeHelper.getSizeByAttribute('height_mm').localized}
          size={'large'}
          tooltip={true}
          translations={data.translations.strings}
          onChange={(e: SizeUpdatedEvent) => onCustomSizeChange(e)}
        ></SizeBox>
      </div>
    {/if}
  </GridItem>
{/snippet}

{#snippet quantity()}
  <GridItem colSpan={responsiveColumns.quantityCol}>
    <div class="flex">
      <HeadingSmall id="quantity-heading" class="pb-3" size="xsmall">
        {TranslationService.getTranslationFromMap(
          data.translations.strings,
          'products',
          'product_wizard',
          'quantity',
        )}
      </HeadingSmall>
      {#if data.includeVat}
        <div class="mx-auto">
          <Checkbox
            onClick={() => {
              states.showVat = !states.showVat;
              evaluate();
            }}
            checked={states.showVat}
            disabled={false}
            id={'vatCheckbox'}
            boxSize={'medium'}
            label={data.translations.strings['inc_vat']}
          />
        </div>
      {/if}
    </div>
    <ListGroupSelectable
      name="price"
      options={states.pricesAndQuantity}
      disabled={staticQuantity ? true : false}
      onChange={(e: ListGroupOption) => onChange('quantity', e)}
      aria-labelledby="quantity-heading"
    ></ListGroupSelectable>
    {#if states.customQuantityOpen || staticQuantity}
      <div class={clsx('flex w-full items-end pt-2')}>
        <Popover
          class="w-full"
          floater={data.translations.strings['tooltip_error_quantity'].replace(
            '$1',
            getMinQuantity().toString(),
          )}
          placement={'bottom'}
          trigger={states.customQuantityInvalid ? 'always' : 'hidden'}
        >
          <ProductWizardTextField
            class={'rounded-none rounded-s-md border-r-0'}
            type="number"
            disabled={staticQuantity ? true : false}
            step={customQuantityStep}
            value={staticQuantity || states.selectedQuantity}
            onChange={(event: Event) => onCustomQuantityChange(event)}
            onBlur={(event: Event) => onCustomQuantityBlur(event)}
            min={getMinQuantity()}
            id={idQuantityInput}
            max={100000000}
          />
        </Popover>
        {#if !staticQuantity}
          <div class={clsx(colors.bgAndBorder, 'border px-3 py-2')}>
            <Text>
              {states.price.totalFormatted}
            </Text>
          </div>
        {/if}
      </div>
    {/if}
  </GridItem>
{/snippet}

<div
  id="wizform"
  role="region"
  class={clsx(
    `mx-auto flex max-w-page-max flex-col justify-center rounded-lg border-2 border-dashed border-transparent sm:flex-col md:flex-col`,
    classList,
    alignmentClass,
    backGroundClass,
  )}
  ondrop={(e) => {
    onDropFile(e);
  }}
  ondragover={(e) => {
    customCSS(e, true);
  }}
  ondragleave={(e) => {
    customCSS(e, false);
  }}
>
  {#if title && title !== ''}
    <HeadingMedium class="pb-3">
      {title}
    </HeadingMedium>
  {/if}
  {#if description && description !== ''}
    <Text element="p" class="pb-12">{description}</Text>
  {/if}
  <Grid {columns} space={verticalAlignment ? 'large' : 'gutter'}>
    {#each colOrder as col}
      {#if col === 'product' && !productShapeColHidden()}
        {@render product()}
      {:else if col === 'shape' && productShapeColHidden()}
        {@render shape()}
      {:else if col === 'material'}
        {@render material()}
      {:else if col === 'laminate' && verticalAlignment}
        {@render laminate()}
      {:else if col === 'size'}
        {@render size()}
      {:else if col === 'quantity'}
        {@render quantity()}
      {/if}
    {/each}
    <GridItem class="col-span-full float-end w-full" colSpan={verticalAlignment ? 1 : 12}>
      <div
        class={clsx(
          'float-end w-full pt-3 lg:flex',
          canSendFileLater ? 'lg:w-1/3' : verticalAlignment ? 'lg:w-full' : 'lg:w-1/4',
        )}
      >
        {#if canSendFileLater}
          <Button
            color="dark"
            class="mb-2 mr-2"
            onClick={onWizardClose}
            full={true}
            size={buttonSize}>{data.translations.strings['send_file_later']}</Button
          >
        {/if}
        {#if states.canUpload || (forceUploadFile && !data.productWizardData.file)}
          <input
            bind:this={fileInput}
            onchange={onUploadFile}
            class="hidden"
            type="file"
            name="files[]"
            accept="image/jpeg, image/png, image/gif, application/pdf, application/postscript, image/svg+xml, image/bmp, image/x-windows-bmp, application/psd, application/x-photoshop, image/photoshop, image/psd, image/x-photoshop, image/x-psd, .psd, image/tiff"
          />
          <Button class="mb-2" onClick={() => fileInput?.click()} full={true} size={buttonSize}
            >{data.translations.strings['upload_file']}</Button
          >
        {:else}
          <Button class="mb-2" onClick={onWizardClose} full={true} size={buttonSize}
            >{data.translations.strings['continue']}</Button
          >
        {/if}
      </div>
    </GridItem>
    <GridItem colSpan={{ lg: 12 }}>
      <ProgressBar progress={states.uploadProgressPercentage} size={'medium'} showText={false}
      ></ProgressBar>
    </GridItem>
  </Grid>
</div>

<style>
  :global(.cards ::-webkit-scrollbar) {
    width: 12px;
    height: 12px;
  }
  :global(.cards ::-webkit-scrollbar-thumb) {
    border-radius: 5px;
    background: lightgrey;
    border: 3px solid transparent;
    background-clip: content-box;
  }
</style>
